import { useEffect, useState } from 'react';
import { WebOCRSDK } from '../../../../sdks/WebOCRSDK';
import axios from 'axios';
import { ChevronRightIcon } from '@radix-ui/react-icons';

import { RGIconComponent } from '../../../PersonalDocument/icons/RgIconComponent';
import { CnhIconComponent } from '../../../PersonalDocument/icons/CnhIconComponent';
import { CnhDigitalIconComponent } from '../../../PersonalDocument/icons/CnhDigitalIconComponent';
import { PassportIconComponent } from '../../../PersonalDocument/icons/PassportIconComponent';
import { RneIconComponent } from '../../../PersonalDocument/icons/RneIconComponent';
import { CtpsIconComponent } from '../../../PersonalDocument/icons/CtpsIconComponent';
import { MilitaryRegistryIconComponent } from '../../../PersonalDocument/icons/MilitaryRegistryIconComponent';
import { OthersIconComponent } from '../../../PersonalDocument/icons/OthersIconComponent';

import { ErrorPage } from '../../../ErrorPage';
import { PrivatePayrollAuctionLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { setDatadogError } from '../../../../services/datadog';
import { resolveColor } from '../../theme/utils/resolveColor';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { PoweredByQITech } from '../components/PoweredByQITech';
import * as S from './styles';

interface PersonalDocumentFolderMode {
    submitOcr: (payload: Record<string, unknown>) => Promise<void>;
}

interface PersonalDocumentProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
    signerHash: string;
    folderMode?: PersonalDocumentFolderMode;
}

const documentTemplates: Record<string, { icon: any; title: string }> = {
    rg: { icon: RGIconComponent, title: 'Cédula de Identidade (RG/CIN)' },
    cnh: { icon: CnhIconComponent, title: 'Carteira de Habilitação (CNH)' },
    cnh_digital: { icon: CnhDigitalIconComponent, title: 'Carteira de Habilitação Digital (CNH)' },
    rg_cin_digital: { icon: RGIconComponent, title: 'Cédula de Identidade Digital (RG/CIN)' },
    cin_digital: { icon: RGIconComponent, title: 'Carteira de Identidade Nacional (CIN)' },
    passport: { icon: PassportIconComponent, title: 'Passaporte' },
    rne: { icon: RneIconComponent, title: 'Registro Nacional de Estrangeiros (RNE)' },
    crnm: { icon: RneIconComponent, title: 'Carteira de Registro Nacional Migratório (CRNM)' },
    ctps: { icon: CtpsIconComponent, title: 'Carteira de Trabalho e Previdência Social (CTPS)' },
    military_registry: { icon: MilitaryRegistryIconComponent, title: 'Cartão Militar de Identificação' },
    others: { icon: OthersIconComponent, title: 'Outros Documentos' },
};

export const PrivatePayrollAuctionPersonalDocument = ({
    signData,
    setSignerStep,
    setSignData,
    signerHash,
    folderMode,
}: PersonalDocumentProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);
    const initialError = { has_error: false, status: 0 };

    const isFolderMode = !!folderMode;
    
    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(true);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [ocrActive, setOcrActive] = useState(false);
    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([loading]);

    const clientDocumentTemplates: string[] = signData.document_templates != null ? signData.document_templates : ['rg', 'cnh'];

    useEffect(() => {
        setLoading(false);
    }, []);

    if (loading) return <PrivatePayrollAuctionLoading />;

    const submitOCRKeys = async (ocrKeys: any[]) => {
        if (hasMadeRequest) return;

        let payload: Record<string, any> = {};

        if (ocrKeys.length === 1) {
            payload = { ocr_full_document_key: ocrKeys[0]['ocr_key'] };
        } else if (ocrKeys.length === 2) {
            const firstOcrKey = ocrKeys[0];
            const secondOcrKey = ocrKeys[1];
            const firstDocumentArray = firstOcrKey['template'].split('_');
            const ocrFrontKey =
                firstDocumentArray[firstDocumentArray.length - 1] === 'front'
                    ? firstOcrKey['ocr_key']
                    : secondOcrKey['ocr_key'];
            const ocrBackKey =
                firstDocumentArray[firstDocumentArray.length - 1] === 'back'
                    ? firstOcrKey['ocr_key']
                    : secondOcrKey['ocr_key'];
            payload = { ocr_front_key: ocrFrontKey, ocr_back_key: ocrBackKey };
        } else {
            setDatadogError('Unsupported ocr key amount', { ocr_key_amount: ocrKeys.length, signData });
        }

        if (isFolderMode) {
            setLoading(true);
            setHasMadeRequest(true);
            try {
                await folderMode!.submitOcr({ ...payload, event_date: new Date().toISOString() });
            } catch {
                setError({ has_error: true, status: 500 });
                setHasMadeRequest(false);
            } finally {
                setLoading(false);
            }
            return;
        }

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        payload = {
            ...payload,
            step: 'personal_document',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((err) => {
                const status = [401, 403, 409].includes(err.response?.status)
                    ? err.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => setLoading(false));
    };

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ScrollContent>
                <S.Title $color={primary}>Envie seu documento</S.Title>
                <S.Subtitle>Documentos aceitos:</S.Subtitle>

                <S.DocumentsGrid>
                    {clientDocumentTemplates.map((type: string, index: number) => {
                        const doc = documentTemplates[type];
                        if (!doc) return null;
                        const IconComponent = doc.icon;
                        return (
                            <S.DocumentCard key={index} onClick={() => { if (!hasScrolledToEnd) { scrollToBottom(); return; } setOcrActive(true); }}>
                                <IconComponent
                                    size={48}
                                    strokeColor={primary}
                                    strokeWidth={1.0}
                                />
                                <S.DocumentTitle>{doc.title}</S.DocumentTitle>
                            </S.DocumentCard>
                        );
                    })}
                </S.DocumentsGrid>
                <div ref={contentEndRef} />
            </S.ScrollContent>

            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}

            <S.Footer>
                <S.SubmitButton
                    $backgroundColor={primary}
                    $disabled={false}
                    onClick={() => { if (!hasScrolledToEnd) { scrollToBottom(); return; } setOcrActive(true); }}
                >
                    Continuar
                    <ChevronRightIcon width={24} height={24} />
                </S.SubmitButton>
                <PoweredByQITech />
            </S.Footer>
            {ocrActive && <WebOCRSDK ocrToken={signData.ocr_token} sessionId={signerHash} clientThemeSettings={clientThemeSettings} documentTypes={clientDocumentTemplates} documentNumber={signData.signer_data?.document_number} imageKey={signData.image_key} reuseDocument={signData.reuse_document} validateLivenessMatch={signData.validate_liveness_match} onSuccess={submitOCRKeys} onError={() => setOcrActive(false)} />}
        </S.PageWrapper>
    );
};
